import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useApi, useAction } from '../lib/hooks.js';
import { Loading, ErrorNote, Modal, Pill } from '../components/ui.jsx';
import AdSlot from '../components/AdSlot.jsx';

const MEALS = ['breakfast', 'lunch', 'dinner'];
const DAY_FORMAT = { weekday: 'short', month: 'numeric', day: 'numeric' };

/** Req #11: plan the week, then shop for it in one pass. */
export default function MealPlan() {
  const plan = useApi('/meal-plans/current');
  const [picking, setPicking] = useState(null);
  const [result, setResult] = useState(null);
  const { busy, error, run } = useAction();

  if (plan.loading && !plan.data) return <Loading rows={7} />;
  if (plan.error) return <ErrorNote error={plan.error} onRetry={plan.reload} />;
  if (!plan.data) return null;

  const data = plan.data;
  const planned = data.entries.filter((entry) => entry.recipe_id).length;

  const removeEntry = (entry) => run(async () => {
    await api.delete(`/meal-plans/${data.id}/entries/${entry.id}`);
    // The build summary -- and its "Add these too" offer -- described a plan
    // that no longer exists. PickRecipe's onSaved already clears it too.
    setResult(null);
    plan.reload();
  });

  const buildList = (body) => run(async () => {
    const response = await api.post(`/meal-plans/${data.id}/shopping-list`, body);
    setResult(response);
  });

  return (
    <div className="stack">
      <ErrorNote error={error} />

      <div className="row-between">
        <div>
          <h2>{data.name}</h2>
          <div className="small muted">{planned} meal{planned === 1 ? '' : 's'} planned</div>
        </div>
      </div>

      {data.days.map((day) => (
        <section key={day.date}>
          <div className="section-title">
            <h2>{new Date(`${day.date}T12:00:00`).toLocaleDateString(undefined, DAY_FORMAT)}</h2>
          </div>
          <div className="card list">
            {MEALS.map((meal) => {
              const entries = day.entries.filter((entry) => entry.meal_type === meal);
              return (
                <div key={meal} className="list-item">
                  <span className="small muted" style={{ width: 68, flex: 'none', textTransform: 'capitalize' }}>
                    {meal}
                  </span>
                  <div className="grow" style={{ minWidth: 0 }}>
                    {entries.length === 0 ? (
                      <button
                        type="button"
                        className="ghost small"
                        onClick={() => setPicking({ date: day.date, meal })}
                      >
                        + add
                      </button>
                    ) : entries.map((entry) => (
                      <div key={entry.id} className="row">
                        {entry.recipe_id ? (
                          <Link className="grow truncate" to={`/recipes/${entry.recipe_id}`}>
                            {entry.recipe_title}
                          </Link>
                        ) : (
                          <span className="grow truncate">{entry.custom_text}</span>
                        )}
                        <button
                          type="button"
                          className="icon ghost"
                          aria-label="Remove"
                          onClick={() => removeEntry(entry)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                  {entries.length > 0 && (
                    <button
                      type="button"
                      className="icon ghost"
                      aria-label={`Add another ${meal}`}
                      onClick={() => setPicking({ date: day.date, meal })}
                    >
                      +
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <hr />

      {result ? (
        <div className="card stack">
          <div className="alert info">
            Analyzed {result.recipes_analyzed} recipe{result.recipes_analyzed === 1 ? '' : 's'}.
            Added {result.needs_added} item{result.needs_added === 1 ? '' : 's'};
            you already have {result.already_have}.
          </div>
          {result.uncertain?.length > 0 && (
            <div className="alert warn small">
              <strong>Couldn&rsquo;t check {result.uncertain.length}:</strong>{' '}
              {result.uncertain.map((entry) => entry.name).join(', ')}.
              <button
                type="button"
                className="small full"
                style={{ marginTop: 8 }}
                disabled={busy}
                // only_uncertain, NOT include_uncertain: the first build already
                // put the gaps on the list, and asking for everything again
                // would double their quantities line by line.
                onClick={() => buildList({ only_uncertain: true })}
              >
                Add these too
              </button>
            </div>
          )}
          <Link className="button primary full" to="/shopping">Open the shopping list</Link>
        </div>
      ) : (
        <button
          type="button"
          className="primary full"
          disabled={busy || planned === 0}
          onClick={() => buildList({})}
        >
          {busy ? <><span className="spinner" /> Checking the pantry</> : 'Build the week’s shopping list'}
        </button>
      )}
      {planned === 0 && <p className="tiny muted center">Add a recipe to a day first.</p>}

      <AdSlot name="meal_plan" />

      {picking && (
        <PickRecipe
          planId={data.id}
          slot={picking}
          onClose={() => setPicking(null)}
          onSaved={() => { setPicking(null); setResult(null); plan.reload(); }}
        />
      )}
    </div>
  );
}

function PickRecipe({ planId, slot, onClose, onSaved }) {
  const [query, setQuery] = useState('');
  const recipes = useApi(`/recipes?limit=40${query ? `&q=${encodeURIComponent(query)}` : ''}`);
  const makeable = useApi('/recipes/makeable?maxMissing=2&limit=8');
  const { busy, error, run } = useAction();

  const add = (recipeId, customText) => run(async () => {
    await api.post(`/meal-plans/${planId}/entries`, {
      plan_date: slot.date,
      meal_type: slot.meal,
      recipe_id: recipeId ?? null,
      custom_text: customText ?? null,
    });
    onSaved();
  });

  return (
    <Modal title={`${slot.meal} on ${slot.date}`} onClose={onClose}>
      <div className="stack">
        <ErrorNote error={error} />

        {!query && makeable.data?.results?.length > 0 && (
          <div>
            <div className="label-text">Ready to cook with what you have</div>
            <div className="card list">
              {makeable.data.results.slice(0, 5).map(({ recipe, summary }) => (
                <button
                  key={recipe.id}
                  type="button"
                  className="ghost list-item"
                  style={{ width: '100%', textAlign: 'left' }}
                  disabled={busy}
                  onClick={() => add(recipe.id)}
                >
                  <span className="grow truncate">{recipe.title}</span>
                  {summary.can_make && <Pill tone="ok">ready</Pill>}
                </button>
              ))}
            </div>
          </div>
        )}

        <input
          type="search"
          placeholder="Search all recipes…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search recipes"
        />

        <div className="card list" style={{ maxHeight: 280, overflowY: 'auto' }}>
          {(recipes.data?.recipes || []).map((recipe) => (
            <button
              key={recipe.id}
              type="button"
              className="ghost list-item"
              style={{ width: '100%', textAlign: 'left' }}
              disabled={busy}
              onClick={() => add(recipe.id)}
            >
              <span className="grow truncate">{recipe.title}</span>
            </button>
          ))}
        </div>

        {query.trim() && (
          <button type="button" className="full" disabled={busy} onClick={() => add(null, query.trim())}>
            Just write &ldquo;{query.trim()}&rdquo;
          </button>
        )}
      </div>
    </Modal>
  );
}
